import { Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';
import { preloadAppRouteModules } from '@/app/routeModules.js';
import { Sheet, SheetContent } from '@/components/ui/sheet.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { Sidebar } from '../Sidebar/index.js';
import { Header } from '../Header/index.js';
import { BottomNav, hidesBottomNav } from '../BottomNav/index.js';
import { useGetProfileQuery } from '@/features/auth/authApi.js';
import { setUser } from '@/features/auth/authSlice.js';
import { useSocket } from '@/shared/hooks/useSocket.js';
import { chatApi } from '@/features/chat/chatApi.js';
import { useIncomingCallListener } from '@/features/calls/useIncomingCallListener.js';
import { IncomingCallSheet, ActiveCallOverlay } from '@/components/calls/index.js';

/**
 * Hook: matches a media query, ssr-safe.
 */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

function PageSkeleton() {
  return (
    <div className="space-y-4 p-4 sm:p-6" aria-busy="true" aria-label="Загрузка раздела">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-72 max-w-full" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-32 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

export function AppLayout() {
  useIncomingCallListener();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { on, connected } = useSocket();
  const isMobile = !useMediaQuery('(min-width: 1024px)'); // mobile = below lg
  const isPhone = !useMediaQuery('(min-width: 640px)'); // for bottom nav padding
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const mainRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    preloadAppRouteModules();
  }, []);

  // Раскладка больше не пересоздаётся при смене раздела, поэтому прокрутку контента
  // сбрасываем сами, иначе новый раздел открывался бы на высоте прошлого.
  useLayoutEffect(() => {
    mainRef.current?.scrollTo(0, 0);
  }, [location.pathname]);

  // Load full user profile on mount
  const { data: profileData } = useGetProfileQuery();

  useEffect(() => {
    if (profileData?.data) {
      dispatch(setUser(profileData.data));
    }
  }, [profileData, dispatch]);

  // Auto-close mobile sidebar on route change
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  // Global socket listener: refresh unread + show sonner toast for new messages
  useEffect(() => {
    if (!connected) return;
    const unsub = on('message:new', (data: any) => {
      dispatch(chatApi.util.invalidateTags(['Conversations']));

      // Don't toast if user is currently in that conversation
      if (location.pathname.includes(`/chat/${data.conversationId}`)) return;
      // Don't toast for own messages
      if (data.senderName == null) return;

      const text =
        typeof data.content === 'string' && data.content.length > 0
          ? data.content.slice(0, 80)
          : 'Новое сообщение';

      toast.message(data.senderName ?? 'Сообщение', {
        description: text,
        action: {
          label: 'Открыть',
          onClick: () => navigate(`/chat/${data.conversationId}`),
        },
      });
    });
    return unsub;
  }, [on, connected, dispatch, location.pathname, navigate]);

  // ServiceWorker → main thread: navigate по клику на пуш.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;
    const onMsg = (e: MessageEvent) => {
      const data = e.data as { type?: string; url?: string } | undefined;
      if (!data || data.type !== 'nav' || typeof data.url !== 'string') return;
      navigate(data.url);
    };
    navigator.serviceWorker.addEventListener('message', onMsg);
    return () => navigator.serviceWorker.removeEventListener('message', onMsg);
  }, [navigate]);

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background">
      {/* Desktop sidebar */}
      {!isMobile && (
        <div className="h-screen shrink-0">
          <Sidebar variant="desktop" />
        </div>
      )}

      {/* Mobile sidebar (Sheet) */}
      {isMobile && (
        <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
          <SheetContent side="left" className="w-[260px] p-0">
            <Sidebar variant="mobile" onNavigate={() => setMobileSidebarOpen(false)} />
          </SheetContent>
        </Sheet>
      )}

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <Header isMobile={isMobile} onMenuClick={() => setMobileSidebarOpen(true)} />

        <main
          ref={mainRef}
          className="flex-1 overflow-y-auto"
          style={{
            paddingBottom:
              isPhone && !hidesBottomNav(location.pathname)
                ? 'calc(64px + env(safe-area-inset-bottom, 0px))'
                : 0,
          }}
        >
          {/* Своя граница: пока грузится страница, сайдбар и шапка остаются на месте. */}
          <Suspense fallback={<PageSkeleton />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* Bottom nav (mobile only — handles its own breakpoint) */}
      <BottomNav />

      {/* Calls UI — render globally; components decide visibility from store */}
      <IncomingCallSheet />
      <ActiveCallOverlay />
    </div>
  );
}
